import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useAuth } from './authContext';
import supabase from '../supabaseClient';
import { useMessagesUnread } from './MessagesUnreadContext.jsx';

const ChatDockContext = createContext(null);

export const useChatDock = () => useContext(ChatDockContext);

export const ChatDockProvider = ({ children }) => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]); // direct only for dock MVP
  const [requestContacts, setRequestContacts] = useState([]); // non-connection DM senders (pending message requests)
  const [acceptedContacts, setAcceptedContacts] = useState([]); // accepted non-connection DM partners behaving like directs
  const [openChats, setOpenChats] = useState([]); // [{id, type:'direct', name, avatar_url, minimized, expanded}]
  const [messagesById, setMessagesById] = useState({}); // contactId -> messages[]
  const [typingById, setTypingById] = useState({}); // contactId or conversation_id -> boolean
  const [onlineById, setOnlineById] = useState({}); // userId -> true
  const [pinnedContacts, setPinnedContacts] = useState([]); // [{contact_id, contact_type, pin_order}]
  const [directContactIds, setDirectContactIds] = useState([]); // accepted connections ids
  const [allowMessagesFromAnyone, setAllowMessagesFromAnyone] = useState(false);

  // partner_id -> 'pending' | 'accepted' | 'rejected'
  const [messageRequestStatuses, setMessageRequestStatuses] = useState({});

  // Track which non-connection DMs have already shown the alert this session
  const dmAlertedRef = useRef(new Set());

  const unreadCtx = useMessagesUnread();
  const unreadCounts = unreadCtx?.unreadCounts || {};
  const bumpUnreadForIncoming = unreadCtx?.bumpUnreadForIncoming;
  const markThreadRead = unreadCtx?.markThreadRead;

  // Load accepted connections (both directions), group memberships, academy groups, and event participants
  const loadContacts = useCallback(async () => {
    if (!user?.id) { setContacts([]); return; }

    const [{ data: outAcc }, { data: inAcc }] = await Promise.all([
      supabase.from('connections').select('connection_id').eq('user_id', user.id).eq('status', 'accepted'),
      supabase.from('connections').select('user_id').eq('connection_id', user.id).eq('status', 'accepted')
    ]);

    const ids = Array.from(new Set([...(outAcc || []).map(r => r.connection_id), ...(inAcc || []).map(r => r.user_id)]));
    const directIdSet = new Set(ids);
    setDirectContactIds(ids);
    let directs = [];
    if (ids.length > 0) {
      const { data: profs } = await supabase
        .from('profiles')
        .select('id, name, email, avatar_url, last_active')
        .in('id', ids);
      directs = (profs || []).map(p => ({ id: p.id, type: 'direct', name: p.name || p.email, avatar_url: p.avatar_url, last_active: p.last_active }));
    }

    const { data: groupMemberships } = await supabase
      .from('group_members')
      .select('group_id, groups(id, name, group_banner_url)')
      .eq('user_id', user.id);
    const groups = (groupMemberships || []).map(gm => ({ id: gm.groups.id, type: 'group', name: gm.groups.name, avatar_url: null, group_banner_url: gm.groups.group_banner_url }));

    const { data: academyMemberships } = await supabase
      .from('academy_group_members')
      .select('group_id, academy_groups!inner(id, name, description)')
      .eq('student_id', user.id);
    const academyGroups = (academyMemberships || []).map(am => ({
      id: am.academy_groups.id,
      type: 'academy_group',
      name: am.academy_groups.name,
      avatar_url: null,
      description: am.academy_groups.description
    }));

    const [{ data: hostedEvents }, { data: attendance }] = await Promise.all([
      supabase
        .from('events')
        .select('id')
        .eq('created_by', user.id),
      supabase
        .from('event_attendees')
        .select('event_id')
        .eq('user_id', user.id)
    ]);

    const eventIds = Array.from(new Set([
      ...(hostedEvents || []).map(e => e.id),
      ...(attendance || []).map(a => a.event_id)
    ]));

    let eventContacts = [];
    if (eventIds.length > 0) {
      const { data: attendeeRows } = await supabase
        .from('event_attendees')
        .select(`
          event_id,
          profiles:profiles!event_attendees_user_id_fkey(
            id, name, email, avatar_url
          )
        `)
        .in('event_id', eventIds);

      const seen = new Set();
      (attendeeRows || []).forEach(row => {
        const profile = row.profiles;
        if (!profile || profile.id === user.id) return;
        if (seen.has(profile.id)) return;
        if (directIdSet.has(profile.id)) return; // already a direct connection contact
        seen.add(profile.id);
        eventContacts.push({
          id: profile.id,
          type: 'event',
          name: profile.name || profile.email,
          email: profile.email,
          avatar_url: profile.avatar_url
        });
      });
    }

    const merged = [...directs, ...groups, ...academyGroups, ...eventContacts];
    const unique = merged.filter((c, i, arr) => i === arr.findIndex(x => x.id === c.id && x.type === c.type));
    setContacts(unique);

    // Any existing request contacts that have become direct connections should be dropped from requests
    if (ids && ids.length > 0) {
      setRequestContacts(prev => prev.filter(rc => !ids.includes(rc.id)));
    }
  }, [user?.id]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Load message request statuses for the current user
  useEffect(() => {
    if (!user?.id) { setMessageRequestStatuses({}); setRequestContacts([]); return; }
    (async () => {
      try {
        const { data, error } = await supabase
          .from('message_requests')
          .select('partner_id, status')
          .eq('user_id', user.id);
        if (!error && data) {
          const map = {};
          (data || []).forEach(r => {
            if (!r.partner_id) return;
            map[r.partner_id] = r.status || 'pending';
          });
          setMessageRequestStatuses(map);
        } else {
          setMessageRequestStatuses({});
        }
      } catch {
        setMessageRequestStatuses({});
      }
    })();
  }, [user?.id]);

  // Hydrate pending and accepted contacts from message_requests
  useEffect(() => {
    if (!user?.id) {
      setRequestContacts([]);
      setAcceptedContacts([]);
      return;
    }

    const pendingIds = Object.entries(messageRequestStatuses)
      .filter(([, status]) => status === 'pending')
      .map(([partnerId]) => partnerId);
    const acceptedIds = Object.entries(messageRequestStatuses)
      .filter(([, status]) => status === 'accepted')
      .map(([partnerId]) => partnerId);

    if (pendingIds.length === 0 && acceptedIds.length === 0) {
      setRequestContacts([]);
      setAcceptedContacts([]);
      return;
    }
    (async () => {
      try {
        const { data: profs, error } = await supabase
          .from('profiles')
          .select('id, name, email, avatar_url')
          .in('id', [...pendingIds, ...acceptedIds]);
        if (!error && profs) {
          const byId = {};
          (profs || []).forEach(p => {
            byId[p.id] = {
              id: p.id,
              type: 'direct',
              name: p.name || p.email,
              email: p.email,
              avatar_url: p.avatar_url,
            };
          });

          setRequestContacts(pendingIds.map(id => byId[id]).filter(Boolean));
          setAcceptedContacts(acceptedIds.map(id => byId[id]).filter(Boolean));
        }
      } catch {
        // ignore
      }
    })();
  }, [user?.id, messageRequestStatuses]);

  // Load allow_messages_from_anyone for current user
  useEffect(() => {
    if (!user?.id) { setAllowMessagesFromAnyone(false); return; }
    (async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('allow_messages_from_anyone')
          .eq('id', user.id)
          .single();
        if (!error && data) {
          setAllowMessagesFromAnyone(data.allow_messages_from_anyone === true);
        }
      } catch {
        // ignore
      }
    })();
  }, [user?.id]);

  // Heartbeat last_active for current user
  useEffect(() => {
    if (!user?.id) return;
    let timer;
    const beat = async () => {
      try {
        await supabase.from('profiles').update({ last_active: new Date().toISOString() }).eq('id', user.id);
      } catch {}
    };
    beat();
    timer = setInterval(beat, 60000);
    const onUnload = () => { beat(); };
    window.addEventListener('beforeunload', onUnload);
    return () => { clearInterval(timer); window.removeEventListener('beforeunload', onUnload); };
  }, [user?.id]);

  const acceptRequest = async (partnerId) => {
    if (!user?.id || !partnerId) return;
    try {
      const { data: existingRows, error: existingError } = await supabase
        .from('message_requests')
        .select('id')
        .eq('user_id', user.id)
        .eq('partner_id', partnerId)
        .limit(1);
      const existing = existingRows && existingRows[0];
      if (!existingError && existing) {
        await supabase
          .from('message_requests')
          .update({ status: 'accepted' })
          .eq('id', existing.id);
      } else {
        await supabase.from('message_requests').insert({
          user_id: user.id,
          partner_id: partnerId,
          status: 'accepted',
        });
      }
      setMessageRequestStatuses(prev => ({ ...prev, [partnerId]: 'accepted' }));
    } catch {
      // ignore
    }
  };

  const rejectRequest = async (partnerId) => {
    if (!user?.id || !partnerId) return;
    try {
      const { data: existingRows, error: existingError } = await supabase
        .from('message_requests')
        .select('id')
        .eq('user_id', user.id)
        .eq('partner_id', partnerId)
        .limit(1);
      const existing = existingRows && existingRows[0];
      if (!existingError && existing) {
        await supabase
          .from('message_requests')
          .update({ status: 'rejected' })
          .eq('id', existing.id);
      } else {
        await supabase.from('message_requests').insert({
          user_id: user.id,
          partner_id: partnerId,
          status: 'rejected',
        });
      }
      setMessageRequestStatuses(prev => ({ ...prev, [partnerId]: 'rejected' }));
    } catch {
      // ignore
    }
  };

  // Presence: online status using Realtime Presence API
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase.channel('chatdock-presence', { config: { presence: { key: `${user.id}` } } });
    channel.on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState();
      // state is an object keyed by presence key; mark any known contacts as online
      const onlineIds = new Set(Object.keys(state || {}));
      setOnlineById(prev => {
        const map = {};
        onlineIds.forEach(id => { map[id] = true; });
        return map;
      });
    });
    channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await channel.track({ user_id: user.id, at: Date.now() });
      }
    });
    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  // Subscribe to new messages globally and route to open chat if relevant
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel('chatdock-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) => {
        const m = payload.new;
        const isGroup = !!m.conversation_id;
        const keyId = isGroup ? m.conversation_id : (m.sender_id === user.id ? m.receiver_id : (m.receiver_id === user.id ? m.sender_id : null));
        if (!keyId) return;
        setMessagesById(prev => {
          const existing = prev[keyId] || [];
          if (m?.id && existing.some(x => x?.id === m.id)) return prev;

          const optimisticIdx = existing.findIndex(x =>
            !x?.id &&
            x?.created_at === m?.created_at &&
            x?.sender_id === m?.sender_id &&
            (x?.content || '') === (m?.content || '') &&
            (x?.media_url || null) === (m?.media_url || null)
          );

          if (optimisticIdx === -1) {
            return { ...prev, [keyId]: [...existing, m] };
          }

          const next = existing.slice();
          next.splice(optimisticIdx, 1);
          return { ...prev, [keyId]: [...next, m] };
        });
        // If no open or minimized window for this chat, bump unread (only when message from others)
        const chat = openChats.find(c => c.id === keyId);
        if ((!chat || chat.minimized) && m.sender_id !== user.id) {
          bumpUnreadForIncoming?.(keyId);
        }

        // If this is a direct message *to* the current user from a non-connection, ensure we have a request contact
        if (!isGroup && m.receiver_id === user.id) {
          const otherId = m.sender_id;
          if (!otherId || otherId === user.id) return;
          const isExistingDirect = contacts.some(c => c.type === 'direct' && c.id === otherId);
          const alreadyDirectOrAccepted = isExistingDirect || messageRequestStatuses[otherId] === 'accepted';
          if (!alreadyDirectOrAccepted) {
            (async () => {
              try {
                // Upsert or reset message_requests row for this sender
                const { data: existingRows, error: existingError } = await supabase
                  .from('message_requests')
                  .select('id, status')
                  .eq('user_id', user.id)
                  .eq('partner_id', otherId)
                  .limit(1);
                const existing = existingRows && existingRows[0];

                if (!existingError && existing) {
                  if (existing.status === 'rejected') {
                    await supabase
                      .from('message_requests')
                      .update({ status: 'pending' })
                      .eq('id', existing.id);
                  }
                } else {
                  await supabase.from('message_requests').insert({
                    user_id: user.id,
                    partner_id: otherId,
                    status: 'pending',
                  });
                }

                setMessageRequestStatuses(prev => ({
                  ...prev,
                  [otherId]: 'pending',
                }));
              } catch {
                // ignore failures here; request metadata is a UX enhancement
              }
            })();
          }
        }
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'messages' }, (payload) => {
        const m = payload.new;
        const isGroup = !!m.conversation_id;
        const keyId = isGroup ? m.conversation_id : (m.sender_id === user.id ? m.receiver_id : (m.receiver_id === user.id ? m.sender_id : null));
        if (!keyId) return;
        setMessagesById(prev => {
          const existing = prev[keyId] || [];
          const idx = existing.findIndex(x => x.id === m.id);
          if (idx === -1) return prev;
          const next = existing.slice();
          next[idx] = { ...existing[idx], ...m };
          return { ...prev, [keyId]: next };
        });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, openChats, contacts, requestContacts, bumpUnreadForIncoming, messageRequestStatuses]);

  // Load pinned contacts
  useEffect(() => {
    if (!user?.id) { setPinnedContacts([]); return; }
    (async () => {
      const { data } = await supabase
        .from('pinned_contacts')
        .select('contact_id, contact_type, pin_order')
        .eq('user_id', user.id)
        .order('pin_order', { ascending: false });
      setPinnedContacts(data || []);
    })();
  }, [user?.id]);

  // Realtime typing indicator using broadcast
  useEffect(() => {
    if (!user?.id) return;
    const typingChannel = supabase.channel('chatdock-typing', { config: { broadcast: { self: false } } });
    typingChannel.on('broadcast', { event: 'typing' }, (payload) => {
      const { from, to, conversation_id } = payload.payload || {};
      // Direct: if I'm target 'to'; Group: if I'm member, we mark by conversation_id
      if (to && to !== user.id) return;
      const key = conversation_id || from; // for direct, key should be the partner id
      if (!key || from === user.id) return;
      setTypingById(prev => ({ ...prev, [key]: true }));
      // clear after 3s
      setTimeout(() => {
        setTypingById(prev => ({ ...prev, [key]: false }));
      }, 3000);
    });
    typingChannel.subscribe();
    return () => { supabase.removeChannel(typingChannel); };
  }, [user?.id]);

  const notifyTyping = (contactId) => {
    if (!user?.id || !contactId) return;
    const meta = contacts.find(c => c.id === contactId) || acceptedContacts.find(c => c.id === contactId);
    const isGroupType = meta?.type === 'group' || meta?.type === 'academy_group';
    const event = isGroupType
      ? { conversation_id: contactId, from: user.id }
      : { from: user.id, to: contactId };
    supabase.channel('chatdock-typing').send({ type: 'broadcast', event: 'typing', payload: event });
  };

  const openChat = async (contactId, options = {}) => {
    if (!user?.id || !contactId) return;
    const { spawnWindow = true } = options;

    // Optionally open or focus a ChatDock window for this contact.
    // MessengerPanel passes spawnWindow: false so it can load messages
    // without creating an overlapping dock window.
    if (spawnWindow) {
      const exists = openChats.some(c => c.id === contactId);
      if (!exists) {
        const contact = contacts.find(c => c.id === contactId)
          || acceptedContacts.find(c => c.id === contactId)
          || requestContacts.find(c => c.id === contactId);
        const entry = contact || { id: contactId, type: 'direct', name: 'User', avatar_url: null };
        setOpenChats(prev => [...prev, { ...entry, minimized: false, expanded: true }]);
      } else {
        setOpenChats(prev => prev.map(c => c.id === contactId ? { ...c, minimized: false } : c));
      }
    }
    // load recent messages if not present
    if (!messagesById[contactId]) {
      const meta = contacts.find(c => c.id === contactId)
        || acceptedContacts.find(c => c.id === contactId)
        || requestContacts.find(c => c.id === contactId);
      let data;
      const isGroupChat = meta?.type === 'group' || meta?.type === 'academy_group';
      // Fetch messages with their reactions
      if (isGroupChat) {
        ({ data } = await supabase
          .from('messages')
          .select('*, message_reactions(id, emoji, user_id)')
          .eq('conversation_id', contactId)
          .order('created_at', { ascending: true }));
      } else {
        ({ data } = await supabase
          .from('messages')
          .select('*, message_reactions(id, emoji, user_id)')
          .or(`and(sender_id.eq.${user.id},receiver_id.eq.${contactId}),and(sender_id.eq.${contactId},receiver_id.eq.${user.id})`)
          .order('created_at', { ascending: true }));
      }
      setMessagesById(prev => ({ ...prev, [contactId]: data || [] }));
    }
    // mark as read on open/focus
    const meta = contacts.find(c => c.id === contactId) || requestContacts.find(c => c.id === contactId);
    if (markThreadRead) {
      await markThreadRead(contactId, meta?.type || 'direct');
    }

    // Message status semantics (direct threads):
    // - delivered_at: set when receiver opens thread
    // Only update messages sent *to* me by the other party.
    try {
      const isGroupChat = meta?.type === 'group' || meta?.type === 'academy_group';
      if (!isGroupChat) {
        const nowIso = new Date().toISOString();
        await supabase
          .from('messages')
          .update({ delivered_at: nowIso })
          .eq('receiver_id', user.id)
          .eq('sender_id', contactId)
          .is('delivered_at', null);
      }
    } catch {
      // ignore
    }
  };

  const markDirectThreadReadIfNeeded = useCallback(async (contactId) => {
    if (!user?.id || !contactId) return;
    try {
      const nowIso = new Date().toISOString();
      await supabase
        .from('messages')
        .update({ read_at: nowIso })
        .eq('receiver_id', user.id)
        .eq('sender_id', contactId)
        .is('read_at', null);
    } catch {
      // ignore
    }
  }, [user?.id]);

  const closeChat = (contactId) => setOpenChats(prev => prev.filter(c => c.id !== contactId));
  const minimizeChat = (contactId) => setOpenChats(prev => prev.map(c => c.id === contactId ? { ...c, minimized: !c.minimized } : c));
  const toggleExpandChat = (contactId) => setOpenChats(prev => prev.map(c => c.id === contactId ? { ...c, expanded: !c.expanded, minimized: false } : c));

  // Optimistic append: add a message to messagesById immediately (before DB confirms)
  const appendMessage = useCallback((contactId, msg) => {
    if (!contactId || !msg) return;
    setMessagesById(prev => {
      const existing = prev[contactId] || [];
      // Avoid duplicates by checking id or created_at+sender_id
      const isDupe = existing.some(m => 
        (m.id && msg.id && m.id === msg.id) ||
        (m.created_at === msg.created_at && m.sender_id === msg.sender_id && m.content === msg.content)
      );
      if (isDupe) return prev;
      return { ...prev, [contactId]: [...existing, msg] };
    });
  }, []);

  const removeOptimisticMessage = useCallback((contactId, msg) => {
    if (!contactId || !msg) return;
    setMessagesById(prev => {
      const existing = prev[contactId] || [];
      const next = existing.filter(m => {
        const sameId = m?.id && msg?.id && m.id === msg.id;
        const sameKey =
          m?.created_at === msg?.created_at &&
          m?.sender_id === msg?.sender_id &&
          (m?.content || '') === (msg?.content || '') &&
          (m?.media_url || null) === (msg?.media_url || null);
        return !(sameId || sameKey);
      });
      if (next.length === existing.length) return prev;
      return { ...prev, [contactId]: next };
    });
  }, []);

  // Delete a message
  const deleteMessage = useCallback(async (contactId, messageId) => {
    if (!messageId || !user?.id) return false;
    try {
      // Only allow deleting own messages
      const { error } = await supabase
        .from('messages')
        .delete()
        .eq('id', messageId)
        .eq('sender_id', user.id);
      if (error) throw error;
      // Optimistically remove from local state
      setMessagesById(prev => {
        const existing = prev[contactId] || [];
        return { ...prev, [contactId]: existing.filter(m => m.id !== messageId) };
      });
      return true;
    } catch (e) {
      console.error('Failed to delete message:', e);
      return false;
    }
  }, [user?.id]);

  // Edit a message
  const editMessage = useCallback(async (contactId, messageId, newContent) => {
    if (!messageId || !user?.id || !newContent?.trim()) return false;
    try {
      const { error } = await supabase
        .from('messages')
        .update({ content: newContent.trim(), edited_at: new Date().toISOString() })
        .eq('id', messageId)
        .eq('sender_id', user.id);
      if (error) throw error;
      // Optimistically update local state
      setMessagesById(prev => {
        const existing = prev[contactId] || [];
        return {
          ...prev,
          [contactId]: existing.map(m =>
            m.id === messageId ? { ...m, content: newContent.trim(), edited_at: new Date().toISOString() } : m
          )
        };
      });
      return true;
    } catch (e) {
      console.error('Failed to edit message:', e);
      return false;
    }
  }, [user?.id]);

  // Add or toggle a reaction on a message
  const addReaction = useCallback(async (messageId, emoji) => {
    if (!user?.id || !messageId || !emoji) {
      console.error('addReaction: missing params', { userId: user?.id, messageId, emoji });
      return false;
    }
    try {
      // With UNIQUE(message_id, user_id):
      // - If no row exists: INSERT
      // - If same emoji exists: DELETE (toggle off)
      // - If different emoji exists: UPDATE (switch reaction)
      const { data: existing, error: selectError } = await supabase
        .from('message_reactions')
        .select('id, emoji')
        .eq('message_id', messageId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (selectError) {
        console.error('addReaction select error:', selectError);
      }

      let success = false;

      if (!existing) {
        const { error: insertError } = await supabase
          .from('message_reactions')
          .insert([{
            message_id: messageId,
            user_id: user.id,
            emoji,
            created_at: new Date().toISOString()
          }]);
        if (insertError) {
          console.error('addReaction insert error:', insertError);
          return false;
        }
        success = true;
      } else if (existing.emoji === emoji) {
        const { error: deleteError } = await supabase
          .from('message_reactions')
          .delete()
          .eq('id', existing.id);
        if (deleteError) {
          console.error('addReaction delete error:', deleteError);
          return false;
        }
        success = true;
      } else {
        const { error: updateError } = await supabase
          .from('message_reactions')
          .update({ emoji })
          .eq('id', existing.id);
        if (updateError) {
          console.error('addReaction update error:', updateError);
          return false;
        }
        success = true;
      }

      // Refresh reactions for this message in local state
      if (success) {
        const { data: updatedReactions } = await supabase
          .from('message_reactions')
          .select('id, emoji, user_id')
          .eq('message_id', messageId);

        setMessagesById(prev => {
          const updated = { ...prev };
          for (const contactId of Object.keys(updated)) {
            updated[contactId] = updated[contactId].map(msg =>
              msg.id === messageId
                ? { ...msg, message_reactions: updatedReactions || [] }
                : msg
            );
          }
          return updated;
        });
      }

      return success;
    } catch (e) {
      console.error('Failed to add reaction:', e);
      return false;
    }
  }, [user?.id]);

  const sendMessage = async (contactId, text, replyToId = null) => {
    if (!user?.id || !contactId || !text?.trim()) {
      return { success: false, error: { message: 'Missing message content.' } };
    }
    const meta = contacts.find(c => c.id === contactId) || requestContacts.find(c => c.id === contactId);
    const isGroup = meta?.type === 'group' || meta?.type === 'academy_group';

    // If this is a direct message to a non-connection, and the recipient does NOT allow messages from anyone,
    // show a one-time alert to the sender so they understand it will be a message request.
    if (!isGroup && meta && meta.id && !contacts.some(c => c.type === 'direct' && c.id === meta.id)) {
      const alreadyAlerted = dmAlertedRef.current.has(meta.id);
      if (!alreadyAlerted) {
        try {
          const { data: recipient, error } = await supabase
            .from('profiles')
            .select('allow_messages_from_anyone')
            .eq('id', meta.id)
            .single();
          if (!error && recipient && recipient.allow_messages_from_anyone === false) {
            dmAlertedRef.current.add(meta.id);
            window.alert('This user does not accept messages from anyone. Your message will be sent as a request and must be accepted before they can read it.');
          }
        } catch {
          // ignore
        }
      }
    }
    const msg = {
      sender_id: user.id,
      receiver_id: isGroup ? null : contactId,
      conversation_id: isGroup ? contactId : null,
      content: text.trim(),
      created_at: new Date().toISOString(),
      reply_to_id: replyToId || null
    };
    // Insert the message; the realtime INSERT listener will append it to messagesById.
    try {
      const { error } = await supabase.from('messages').insert([msg]);
      if (error) return { success: false, error };
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  };

  const pinContact = async (contactId, contactType = 'direct') => {
    if (!user?.id || !contactId) return;
    const maxOrder = pinnedContacts.length > 0 ? Math.max(...pinnedContacts.map(p => p.pin_order || 0)) : 0;
    const { error } = await supabase
      .from('pinned_contacts')
      .insert({ 
        user_id: user.id, 
        contact_id: contactId, 
        contact_type: contactType,
        pin_order: maxOrder + 1
      });
    if (!error) {
      setPinnedContacts(prev => [...prev, { contact_id: contactId, contact_type: contactType, pin_order: maxOrder + 1 }]);
    }
  };

  const unpinContact = async (contactId, contactType = 'direct') => {
    if (!user?.id || !contactId) return;
    const { error } = await supabase
      .from('pinned_contacts')
      .delete()
      .eq('user_id', user.id)
      .eq('contact_id', contactId)
      .eq('contact_type', contactType);
    if (!error) {
      setPinnedContacts(prev => prev.filter(p => !(p.contact_id === contactId && p.contact_type === contactType)));
    }
  };

  // Sorted contacts with pinned ones first, ordered by latest activity
  const sortedContacts = useMemo(() => {
    if (!contacts || contacts.length === 0) return [];

    const pinnedSet = new Set(pinnedContacts.map(p => `${p.contact_id}_${p.contact_type}`));

    const getLastActivity = (c) => {
      const msgs = messagesById[c.id];
      if (msgs && msgs.length > 0) {
        const last = msgs[msgs.length - 1];
        if (last?.created_at) {
          return new Date(last.created_at).getTime();
        }
      }
      if (c.last_active) {
        return new Date(c.last_active).getTime();
      }
      return 0;
    };

    const sortByActivity = (a, b) => getLastActivity(b) - getLastActivity(a);

    // Base contacts: core contacts + accepted DM partners, plus (optionally) pending requests when allow-from-anyone is on.
    const baseCore = [...contacts, ...acceptedContacts];
    const base = allowMessagesFromAnyone
      ? [...baseCore, ...requestContacts]
      : baseCore;

    const deduped = base.filter((c, i, arr) => i === arr.findIndex(x => x.id === c.id && x.type === c.type));

    const pinned = deduped
      .filter(c => pinnedSet.has(`${c.id}_${c.type}`))
      .sort(sortByActivity);
    const unpinned = deduped
      .filter(c => !pinnedSet.has(`${c.id}_${c.type}`))
      .sort(sortByActivity);

    return [...pinned, ...unpinned];
  }, [contacts, acceptedContacts, requestContacts, pinnedContacts, messagesById, allowMessagesFromAnyone]);

  // Sorted request contacts list (for the Requests tab), same pin + activity ordering
  const sortedRequestContacts = useMemo(() => {
    if (!requestContacts || requestContacts.length === 0) return [];

    const pinnedSet = new Set(pinnedContacts.map(p => `${p.contact_id}_${p.contact_type}`));

    const getLastActivity = (c) => {
      const msgs = messagesById[c.id];
      if (msgs && msgs.length > 0) {
        const last = msgs[msgs.length - 1];
        if (last?.created_at) {
          return new Date(last.created_at).getTime();
        }
      }
      return 0;
    };

    const sortByActivity = (a, b) => getLastActivity(b) - getLastActivity(a);

    const pinned = requestContacts
      .filter(c => pinnedSet.has(`${c.id}_${c.type}`))
      .sort(sortByActivity);
    const unpinned = requestContacts
      .filter(c => !pinnedSet.has(`${c.id}_${c.type}`))
      .sort(sortByActivity);

    return [...pinned, ...unpinned];
  }, [requestContacts, pinnedContacts, messagesById]);

  const value = useMemo(() => ({
    contacts,
    sortedContacts,
    requestContacts,
    sortedRequestContacts,
    openChats,
    messagesById,
    unreadCounts,
    typingById,
    onlineById,
    pinnedContacts,
    allowMessagesFromAnyone,
    messageRequestStatuses,
    openChat,
    closeChat,
    minimizeChat,
    toggleExpandChat,
    notifyTyping,
    sendMessage,
    appendMessage,
    removeOptimisticMessage,
    deleteMessage,
    editMessage,
    addReaction,
    markDirectThreadReadIfNeeded,
    pinContact,
    unpinContact,
    acceptRequest,
    rejectRequest,
    reloadContacts: loadContacts,
  }), [contacts, sortedContacts, requestContacts, sortedRequestContacts, openChats, messagesById, unreadCounts, typingById, onlineById, pinnedContacts, allowMessagesFromAnyone, messageRequestStatuses, loadContacts, appendMessage, removeOptimisticMessage, deleteMessage, editMessage, addReaction, markDirectThreadReadIfNeeded]);

  return (
    <ChatDockContext.Provider value={value}>
      {children}
    </ChatDockContext.Provider>
  );
};
